import React, { useState, useEffect, useCallback } from 'react'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  FilmSlateIcon,
  X,
  Plus,
  ArrowCounterClockwise,
} from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Link } from 'react-router-dom'

// ─── Helpers ────────────────────────────────────────────────────────────────
const PLATFORM_COLORS = {
  YouTube: '#FF0000',
  Instagram: '#E1306C',
  TikTok: '#69C9D0',
  LinkedIn: '#0077B5',
}
const STATUS_COLORS = {
  idea: '#FFD60A',
  plan: '#5E5CE6',
  record: '#D42B1E',
  edit: '#FF9F0A',
  analyze: '#007AFF',
  publish: '#32ADE6',
}
const DOW_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
const MONTHS_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
]

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
function isToday(date) {
  return toDateStr(date) === toDateStr(new Date())
}
function isSameMonth(date, ref) {
  return date.getMonth() === ref.getMonth() && date.getFullYear() === ref.getFullYear()
}

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1)
  const last = new Date(year, month + 1, 0)
  const leadDow = (first.getDay() + 6) % 7 // Mon=0
  const cells = []
  for (let i = leadDow - 1; i >= 0; i--) cells.push(new Date(year, month, -i))
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d))
  while (cells.length < 42)
    cells.push(new Date(year, month + 1, cells.length - leadDow - last.getDate() + 1))
  return cells
}

function getWeekDays(pivot) {
  const d = new Date(pivot)
  const dow = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - dow)
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d)
    x.setDate(d.getDate() + i)
    return x
  })
}

// ─── Draggable project card (used in unscheduled list + calendar cells) ─────
function DraggableCard({ project, onUnschedule, compact = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: project.id,
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab',
  }
  const color = PLATFORM_COLORS[project.platform] || '#A1A1A6'

  if (compact) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className="group flex items-center gap-1.5 px-2 py-1  bg-ds-elevated border border-ds-border hover:border-ds-border-mid transition-all text-xs"
        data-testid={`cal-card-${project.id}`}
      >
        <div className="w-1.5 h-1.5  shrink-0" style={{ background: color }} />
        <span className="text-white truncate flex-1 text-[11px] leading-tight">
          {project.title}
        </span>
        {onUnschedule && (
          <button
            onClick={e => {
              e.stopPropagation()
              onUnschedule(project.id)
            }}
            className="shrink-0 opacity-0 group-hover:opacity-100 text-[#6E6E73] hover:text-[#D42B1E] transition-all"
            data-testid={`unschedule-${project.id}`}
          >
            <X size={10} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="group flex items-center gap-2 px-3 py-2  bg-ds-elevated border border-ds-border hover:border-ds-border-mid transition-all"
      data-testid={`unscheduled-card-${project.id}`}
    >
      <div className="w-2 h-2  shrink-0" style={{ background: color }} />
      <div className="flex-1 min-w-0">
        <p className="text-white text-xs font-medium truncate">{project.title}</p>
        <p className="text-[#6E6E73] text-[10px]">{project.platform}</p>
      </div>
      <div
        className="w-1.5 h-1.5  shrink-0"
        style={{
          background: STATUS_COLORS[project.status] || '#6E6E73',
        }}
      />
    </div>
  )
}

// ─── Droppable day cell ───────────────────────────────────────────────────────
function DroppableDayCell({ date, projects, currentMonth, onClick, onUnschedule, view }) {
  const dateStr = toDateStr(date)
  const { isOver, setNodeRef } = useDroppable({ id: dateStr })
  const today = isToday(date)
  const inMonth = isSameMonth(date, currentMonth)
  const isWeekend = [5, 6].includes((date.getDay() + 6) % 7)

  if (view === 'week') {
    return (
      <div
        ref={setNodeRef}
        className={`flex flex-col min-h-[120px] border-r border-ds-border transition-colors ${isOver ? 'bg-[#D42B1E]/5' : 'bg-transparent'}`}
        data-testid={`day-cell-${dateStr}`}
      >
        <div
          className={`flex items-center justify-between px-3 py-2 border-b border-ds-border ${today ? 'bg-[#D42B1E]/10' : ''}`}
        >
          <div>
            <p className={`text-xs font-medium ${today ? 'text-[#D42B1E]' : 'text-[#6E6E73]'}`}>
              {DOW_LABELS[(date.getDay() + 6) % 7]}
            </p>
            <p
              className={`text-lg font-bold leading-none mt-0.5 ${today ? 'text-[#D42B1E]' : 'text-white'}`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {date.getDate()}
            </p>
          </div>
          <button
            onClick={() => onClick(date)}
            className="w-6 h-6  bg-white/5 hover:bg-[#D42B1E]/20 flex items-center justify-center text-[#6E6E73] hover:text-[#D42B1E] transition-colors opacity-0 hover:opacity-100 group-hover:opacity-100"
            data-testid={`add-to-day-${dateStr}`}
          >
            <Plus size={12} />
          </button>
        </div>
        <div className="flex flex-col gap-1 p-2 flex-1">
          {projects.map(p => (
            <DraggableCard key={p.id} project={p} onUnschedule={onUnschedule} compact />
          ))}
          {isOver && projects.length === 0 && (
            <div className="flex-1 border-2 border-dashed border-[#D42B1E]/30 " />
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      onClick={() => onClick(date)}
      className={`relative flex flex-col p-1.5 border border-ds-border min-h-[90px] cursor-pointer transition-all
        ${isOver ? 'bg-[#D42B1E]/8 border-[#D42B1E]/30' : today ? 'bg-[#D42B1E]/5 border-[#D42B1E]/25' : isWeekend && inMonth ? 'bg-[#0E0E0E]' : 'bg-ds-elevated'}
        ${!inMonth ? 'opacity-35' : ''}
        hover:border-ds-border-mid`}
      data-testid={`day-cell-${dateStr}`}
    >
      <span
        className={`text-xs font-bold mb-1 w-6 h-6 flex items-center justify-center  ${today ? 'bg-[#D42B1E] text-white' : 'text-[#A1A1A6]'}`}
      >
        {date.getDate()}
      </span>
      <div className="flex flex-col gap-0.5">
        {projects.slice(0, 3).map(p => (
          <DraggableCard key={p.id} project={p} onUnschedule={onUnschedule} compact />
        ))}
        {projects.length > 3 && (
          <span className="text-[#6E6E73] text-[10px] px-1">+{projects.length - 3} más</span>
        )}
      </div>
      {isOver && (
        <div className="absolute inset-0 border-2 border-dashed border-[#D42B1E]/50  pointer-events-none" />
      )}
    </div>
  )
}

// ─── Schedule dialog ──────────────────────────────────────────────────────────
function ScheduleDialog({ date, unscheduled, onSchedule, onClose }) {
  const [selectedId, setSelectedId] = useState('')
  if (!date) return null
  const label = date.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  return (
    <Dialog open={!!date} onOpenChange={onClose}>
      <DialogContent
        className="bg-ds-elevated border border-ds-border text-white max-w-sm"
        data-testid="schedule-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-white font-semibold capitalize"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Programar para {label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {unscheduled.length === 0 ? (
            <p className="text-[#6E6E73] text-sm text-center py-4">
              Todos los proyectos tienen fecha asignada
            </p>
          ) : (
            <>
              <select
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                className="w-full bg-ds-elevated border border-ds-border  px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#D42B1E]"
                data-testid="schedule-project-select"
              >
                <option value="">— Seleccionar proyecto —</option>
                {unscheduled.map(p => (
                  <option key={p.id} value={p.id}>{`${p.title} (${p.platform})`}</option>
                ))}
              </select>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-2 border border-ds-border  text-[#A1A1A6] text-sm hover:text-white"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (selectedId) {
                      onSchedule(selectedId, date)
                      onClose()
                    }
                  }}
                  disabled={!selectedId}
                  className="flex-1 py-2 bg-[#D42B1E] text-white  text-sm font-semibold disabled:opacity-40 hover:bg-[#E0352B] transition-colors"
                  data-testid="confirm-schedule-btn"
                >
                  Programar
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Unscheduled panel ────────────────────────────────────────────────────────
function UnscheduledPanel({ projects }) {
  const [search, setSearch] = useState('')
  const filtered = projects.filter(p => p.title.toLowerCase().includes(search.toLowerCase()))

  return (
    <div
      className="w-[240px] shrink-0 flex flex-col border-l border-ds-border bg-ds-bg"
      data-testid="unscheduled-panel"
    >
      <div className="px-4 py-3 border-b border-ds-border">
        <p className="text-white text-xs font-semibold uppercase tracking-widest mb-2">
          Sin programar
        </p>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar..."
          className="w-full bg-ds-elevated border border-ds-border  px-2.5 py-1.5 text-white text-xs placeholder-[#4A4A4A] focus:outline-none focus:ring-1 focus:ring-[#D42B1E]"
          data-testid="unscheduled-search"
        />
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {filtered.length === 0 ? (
          <p className="text-[#4A4A4A] text-xs text-center py-6">
            {search ? 'Sin resultados' : 'Todos programados'}
          </p>
        ) : (
          filtered.map(p => <DraggableCard key={p.id} project={p} />)
        )}
      </div>
      <div className="px-4 py-3 border-t border-ds-border">
        <p className="text-[#6E6E73] text-[10px] text-center">Arrastra a un día para programar</p>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CalendarPage() {
  const [view, setView] = useState('month')
  const [pivot, setPivot] = useState(new Date())
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeProject, setActiveProject] = useState(null)
  const [scheduleDate, setScheduleDate] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    })
  )

  const fetchProjects = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/projects')
      setProjects(res.data)
    } catch {
      toast.error('Error al cargar proyectos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  const scheduleProject = async (projectId, date) => {
    const dateStr = toDateStr(date)
    setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, scheduled_date: dateStr } : p)))
    try {
      await api.put(`/projects/${projectId}`, {
        scheduled_date: dateStr,
      })
      toast.success(
        `Programado para el ${date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}`
      )
    } catch {
      toast.error('Error al programar')
      fetchProjects()
    }
  }

  const unscheduleProject = async projectId => {
    setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, scheduled_date: null } : p)))
    try {
      await api.put(`/projects/${projectId}`, { scheduled_date: null })
      toast.success('Fecha eliminada')
    } catch {
      toast.error('Error al quitar fecha')
      fetchProjects()
    }
  }

  const handleDragStart = ({ active }) => setActiveProject(projects.find(p => p.id === active.id))
  const handleDragEnd = ({ active, over }) => {
    setActiveProject(null)
    if (!over) return
    const dateStr = over.id
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return
    const [y, m, d] = dateStr.split('-').map(Number)
    scheduleProject(active.id, new Date(y, m - 1, d))
  }

  const navigatePrev = () => {
    if (view === 'month') setPivot(p => new Date(p.getFullYear(), p.getMonth() - 1, 1))
    else
      setPivot(p => {
        const d = new Date(p)
        d.setDate(p.getDate() - 7)
        return d
      })
  }
  const navigateNext = () => {
    if (view === 'month') setPivot(p => new Date(p.getFullYear(), p.getMonth() + 1, 1))
    else
      setPivot(p => {
        const d = new Date(p)
        d.setDate(p.getDate() + 7)
        return d
      })
  }
  const goToday = () => setPivot(new Date())

  const projectsForDate = dateStr => projects.filter(p => p.scheduled_date === dateStr)
  const unscheduled = projects.filter(p => !p.scheduled_date)
  const scheduled = projects.filter(p => !!p.scheduled_date)

  const headerLabel =
    view === 'month'
      ? `${MONTHS_ES[pivot.getMonth()]} ${pivot.getFullYear()}`
      : (() => {
          const days = getWeekDays(pivot)
          return `${days[0].getDate()} – ${days[6].getDate()} ${MONTHS_ES[days[6].getMonth()]} ${days[6].getFullYear()}`
        })()

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-ds-border shrink-0 gap-4">
        <div className="flex items-center gap-3">
          <CalendarBlank size={20} className="text-[#D42B1E]" weight="duotone" />
          <h1
            className="text-xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Calendario de Publicación
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Stats pills */}
          <div className="hidden sm:flex items-center gap-3 mr-2">
            <span className="text-[#6E6E73] text-xs">{scheduled.length} programados</span>
            <span className="text-[#6E6E73] text-xs">·</span>
            <span className="text-[#6E6E73] text-xs">{unscheduled.length} sin fecha</span>
          </div>
          {/* View toggle */}
          <div className="flex bg-ds-elevated  p-0.5">
            {['month', 'week'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5  text-xs font-medium transition-all ${view === v ? 'bg-[#D42B1E] text-white' : 'text-[#A1A1A6] hover:text-white'}`}
                data-testid={`view-${v}`}
              >
                {v === 'month' ? 'Mes' : 'Semana'}
              </button>
            ))}
          </div>
          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={navigatePrev}
              className="p-2  text-[#A1A1A6] hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-prev"
            >
              <CaretLeft size={16} />
            </button>
            <span
              className="text-white text-sm font-medium min-w-[160px] text-center"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {headerLabel}
            </span>
            <button
              onClick={navigateNext}
              className="p-2  text-[#A1A1A6] hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-next"
            >
              <CaretRight size={16} />
            </button>
          </div>
          <button
            onClick={goToday}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#A1A1A6] hover:text-white border border-ds-border  hover:bg-white/5 transition-colors"
            data-testid="today-btn"
          >
            <ArrowCounterClockwise size={13} />
            Hoy
          </button>
        </div>
      </div>

      {/* Calendar + Panel */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#D42B1E] border-t-transparent  animate-spin" />
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex flex-1 min-h-0">
            {/* Calendar area */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              {view === 'month' ? (
                <>
                  {/* DOW headers */}
                  <div className="grid grid-cols-7 border-b border-ds-border shrink-0">
                    {DOW_LABELS.map(d => (
                      <div
                        key={d}
                        className="py-2 text-center text-[#6E6E73] text-xs font-semibold uppercase tracking-widest"
                      >
                        {d}
                      </div>
                    ))}
                  </div>
                  {/* Month grid */}
                  <div className="flex-1 overflow-y-auto">
                    <div
                      className="grid grid-cols-7 h-full"
                      style={{
                        gridAutoRows: 'minmax(90px, 1fr)',
                      }}
                      data-testid="month-grid"
                    >
                      {getMonthGrid(pivot.getFullYear(), pivot.getMonth()).map((date, i) => {
                        const ds = toDateStr(date)
                        return (
                          <DroppableDayCell
                            key={i}
                            date={date}
                            projects={projectsForDate(ds)}
                            currentMonth={pivot}
                            onClick={setScheduleDate}
                            onUnschedule={unscheduleProject}
                            view="month"
                          />
                        )
                      })}
                    </div>
                  </div>
                </>
              ) : (
                /* Week view */
                <div className="flex flex-1 min-h-0 overflow-hidden" data-testid="week-grid">
                  <div className="flex flex-1 overflow-x-auto">
                    {getWeekDays(pivot).map((date, i) => {
                      const ds = toDateStr(date)
                      return (
                        <div key={i} className="flex-1 min-w-[100px] group">
                          <DroppableDayCell
                            date={date}
                            projects={projectsForDate(ds)}
                            currentMonth={pivot}
                            onClick={setScheduleDate}
                            onUnschedule={unscheduleProject}
                            view="week"
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Unscheduled panel - hidden on mobile */}
            <div className="hidden lg:flex">
              <UnscheduledPanel projects={unscheduled} />
            </div>
          </div>

          <DragOverlay>
            {activeProject ? (
              <div className="bg-ds-elevated border border-[#D42B1E]/40  px-3 py-2 shadow-black/50 rotate-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 "
                    style={{
                      background: PLATFORM_COLORS[activeProject.platform] || '#A1A1A6',
                    }}
                  />
                  <span className="text-white text-xs font-medium">{activeProject.title}</span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <ScheduleDialog
        date={scheduleDate}
        unscheduled={unscheduled}
        onSchedule={scheduleProject}
        onClose={() => setScheduleDate(null)}
      />
    </div>
  )
}
